import type {
    DynamicPage_jsonld_page_read,
    PageComponent,
} from '@/core/features/a-dynamic-page/dynamic-page-pacts/dynamic-page-type';

import { isNestedComponent } from '@/core/features/a-component/services/component-service';
import { useDynamicPageQueryData } from '@/core/features/a-dynamic-page/hooks/use-dynamic-page-query';

export default function useDynamicPageComponentByType<T extends PageComponent>(
    type: T['@type'],
    additionalCondition: (component: T) => boolean = () => true,
): T | null {
    const { data } = useDynamicPageQueryData();
    return findComponent<T>(data?.response, type, additionalCondition);
}

const findComponent = <T extends PageComponent>(
    data: DynamicPage_jsonld_page_read | undefined,
    type: T['@type'],
    additionalCondition: (component: T) => boolean = () => true,
): T | null => {
    if (!data?.components) {
        return null;
    }

    return (
        flattenComponents(data.components).find<T>((component): component is T => {
            return componentTypeCheck<T>(component, type) && additionalCondition(component);
        }) ?? null
    );
};

const componentTypeCheck = <T extends PageComponent>(component: PageComponent, type: T['@type']): component is T => {
    return component['@type'] === type;
};

const flattenComponents = (components: PageComponent[]): PageComponent[] => {
    return components.flatMap((component) => {
        if (isNestedComponent(component)) {
            return [component, ...flattenComponents(component.attributes.components)];
        }
        return component;
    });
};
